import type { FC } from 'react';
import Toggle from '@components/Toggle';
import MODALS from '@constants/modals';
import type { BlockVisibilityWarningsProps } from '@ContractBuilder/components/Modal/BlockVisibilityWarnings';
import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';
import { useModalDispatch } from '@hooks/useModalProvider';
import { useVariationsTabs } from '@providers/variations-tabs';

export interface VariationToggleProps {
  formValues?: BlockEditFormState;
  label: string;
}

type ModalProps = Omit<BlockVisibilityWarningsProps, 'handleClose'>;

const blockHasConditionalVisibility = (values?: BlockEditFormState) => !!values?.visibility?.block;
const variantsHaveConditionalVisibility = (values?: BlockEditFormState) => !!values?.visibility?.variations?.length;

export const VariationToggle: FC<VariationToggleProps> = ({ formValues, label }) => {
  const { variationsCount, create, removeAll } = useVariationsTabs();
  const { showModal } = useModalDispatch();

  const hasVariations = !!variationsCount;

  const turnOffVariations = () => {
    if (variantsHaveConditionalVisibility(formValues)) {
      showModal(MODALS.BLOCK_VISIBILITY_WARNING_MODAL, {
        mode: 'variations',
        onDisregardWarning: removeAll,
        isOpen: true,
      } satisfies ModalProps);
      return;
    }

    removeAll();
  };

  const turnOnVariations = () => {
    if (blockHasConditionalVisibility(formValues)) {
      showModal(MODALS.BLOCK_VISIBILITY_WARNING_MODAL, {
        mode: 'block',
        isOpen: true,
        onDisregardWarning: create,
      } satisfies ModalProps);
      return;
    }

    create();
  };

  const toggleVariations = hasVariations ? turnOffVariations : turnOnVariations;

  return <Toggle labelText={label} data-testid="toggle-variations" value={hasVariations} onClick={toggleVariations} />;
};
